import React, { useState, useEffect } from 'react';
import { COLORS } from '../utils/constants';
import { userAPI, notificationsAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

// Wallet Components
import { 
  WalletBalanceCard, 
  WalletTabSwitcher,
  TransactionHistoryList,
  DepositConfirmationModal
} from '../components/wallet';
import { 
  CurrencySelector, 
  QRCodeDisplay, 
  AddressDisplay 
} from '../components/shared';

// Icons
import { 
  Copy, CheckCircle, Send, ArrowUpRight, AlertTriangle, RefreshCw
} from 'lucide-react';

const WalletPage = () => {
  const { t } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('deposit');
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [settings, setSettings] = useState(null);
  const [balance, setBalance] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('ETH');
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [refreshingBalance, setRefreshingBalance] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  const currencies = [
    { value: 'ETH', label: 'ETH', chain: 'Ethereum', canDirect: true },
    { value: 'USDT-ERC20', label: 'USDT', chain: 'ERC20', canDirect: true },
    { value: 'USDT-TRC20', label: 'USDT', chain: 'TRC20', canDirect: false },
    { value: 'BTC', label: 'BTC', chain: 'Bitcoin', canDirect: false },
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load notifications count
  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const res = await notificationsAPI.getUnreadCount();
        setUnreadNotifications(res.data?.count || 0);
      } catch (err) {
        // Ignore notification count errors
      }
    };
    loadUnreadCount();
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [depositsRes, withdrawalsRes, addressesRes, balanceRes] = await Promise.all([
        userAPI.getDeposits(),
        userAPI.getWithdrawals(),
        userAPI.getDepositAddresses().catch(() => ({ data: {} })),
        userAPI.getBalance(),
      ]);
      setDeposits(depositsRes.data);
      setWithdrawals(withdrawalsRes.data);
      setSettings(addressesRes.data);
      setBalance(balanceRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const getDepositAddress = () => {
    switch (selectedCurrency) {
      case 'USDT-TRC20': return settings?.deposit_address_usdt_trc20 || 'Not configured';
      case 'BTC': return settings?.deposit_address_btc || 'Not configured';
      default: return settings?.deposit_address_eth || 'Not configured';
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRefreshBalance = async () => {
    setRefreshingBalance(true);
    try { await loadData(); } 
    finally { setRefreshingBalance(false); }
  };

  // Open deposit confirmation modal
  const handleDepositClick = () => {
    console.log('handleDepositClick called', { amount, selectedCurrency });
    if (!amount || parseFloat(amount) <= 0) { 
      console.log('Invalid amount');
      alert(t('enterValidAmount') || 'Введите корректную сумму'); 
      return; 
    }
    const depositAddr = getDepositAddress();
    console.log('Deposit address:', depositAddr);
    if (depositAddr === 'Not configured') { 
      console.log('Address not configured');
      alert(t('depositAddressNotConfigured') || 'Адрес депозита не настроен'); 
      return; 
    }
    console.log('Setting showDepositModal to true');
    setShowDepositModal(true);
    console.log('showDepositModal should be true now');
  };

  // Add debug for modal render
  console.log('WalletPage render, showDepositModal:', showDepositModal);

  // Handle deposit confirmation from modal
  const handleDepositConfirm = async (depositData) => {
    // Manual deposit with TX Hash
    setSending(true);
    try {
      let currency = selectedCurrency;
      if (selectedCurrency.includes('USDT')) currency = 'USDT';
      
      await userAPI.createDeposit({ 
        amount: parseFloat(depositData.amount || amount), 
        currency, 
        tx_hash: depositData.txHash 
      });
      
      setAmount('');
      setShowDepositModal(false);
      await loadData();
    } catch (error) {
      console.error('Deposit error:', error);
      throw error;
    } finally { 
      setSending(false); 
    }
  };

  const handleWithdrawSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) { alert(t('enterValidAmount')); return; }
    if (!address) { alert(t('enterAddress')); return; }
    if (parseFloat(amount) > (balance?.exchange_balance || 0)) { alert(t('insufficientBalance')); return; }

    setLoading(true);
    try {
      await userAPI.createWithdrawal({ amount: parseFloat(amount), currency: 'USDT', address });
      alert(t('withdrawalRequestCreated'));
      setAmount(''); setAddress('');
      await loadData();
    } catch (error) {
      alert(error.response?.data?.detail || t('failedToCreateWithdrawal'));
    } finally { setLoading(false); }
  };

  const depositAddress = getDepositAddress();
  const quickAmounts = [25, 50, 75, 100];

  // ==================== MOBILE LAYOUT ====================
  if (isMobile) {
    return (
      <div style={{ 
        minHeight: '100%', 
        paddingBottom: '100px',
        paddingTop: '12px',
        backgroundColor: '#0b0e11',
      }}>
        {/* Balance Card - Simplified, no wallet connection */}
        <WalletBalanceCard
          balance={balance}
          connectedWallets={[]}
          ethBalance="0"
          formatAddress={(addr) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : ''}
          onRefresh={handleRefreshBalance}
          onConnectWallet={() => {}}
          refreshing={refreshingBalance}
          t={t}
          isMobile={true}
        />

        {/* Main Tab Switcher */}
        <WalletTabSwitcher
          activeTab={activeTab}
          onTabChange={setActiveTab}
          t={t}
          isMobile={true}
        />

        {/* DEPOSIT TAB */}
        {activeTab === 'deposit' && (
          <>
            {/* Network Selection */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                color: '#848e9c', 
                fontSize: '12px', 
                fontWeight: '500', 
                marginBottom: '8px' 
              }}>
                {t('selectNetwork')}
              </label>
              <CurrencySelector
                currencies={currencies}
                selectedCurrency={selectedCurrency}
                onSelect={setSelectedCurrency}
                isMobile={true}
              />
            </div>

            {/* Deposit Address */}
            <div style={{
              backgroundColor: '#1e2329',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '12px' 
              }}>
                <span style={{ color: '#848e9c', fontSize: '12px' }}>{t('depositAddress')}</span>
                <span style={{
                  padding: '3px 8px',
                  backgroundColor: '#2b3139',
                  borderRadius: '4px',
                  fontSize: '10px',
                  color: '#f0b90b',
                  fontWeight: '600',
                }}>
                  {"ETH"}
                </span>
              </div>
              
              {/* QR Code */}
              <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                <QRCodeDisplay 
                  address={depositAddress} 
                  size={140} 
                  notConfiguredText={t('notConfigured') || 'Not configured'} 
                />
              </div>

              {/* Address Display */}
              <AddressDisplay
                address={depositAddress}
                chain={"ETH"}
                copied={copied}
                onCopy={handleCopy}
                isMobile={true}
              />
            </div>

            {/* Amount Input */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                color: '#848e9c', 
                fontSize: '12px', 
                fontWeight: '500', 
                marginBottom: '8px' 
              }}>
                {t('amount')}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '14px',
                    paddingRight: false ? '70px' : '14px',
                    backgroundColor: '#1e2329',
                    border: '1px solid #2b3139',
                    borderRadius: '10px',
                    color: '#eaecef',
                    fontSize: '16px',
                    fontWeight: '600',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                {false && (
                  <button
                    onClick={() => {
                      const max = parseFloat("0");
                      setAmount(selectedCurrency === 'ETH' ? (max * 0.95).toFixed(6) : max.toFixed(2));
                    }}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      padding: '5px 10px',
                      backgroundColor: '#2b3139',
                      color: '#f0b90b',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    MAX
                  </button>
                )}
              </div>
              {false && (
                <div style={{ marginTop: '6px', fontSize: '11px', color: '#848e9c' }}>
                  {t('available')}: {"0"} {selectedCurrency === 'ETH' ? 'ETH' : 'USDT'}
                </div>
              )}
            </div>

            {/* Action Button - Opens Confirmation Modal */}
            <button
              onClick={handleDepositClick}
              disabled={sending || loading || !amount}
              style={{
                width: '100%',
                padding: '14px',
                background: false 
                  ? 'linear-gradient(180deg, #0ecb81 0%, #0ba360 100%)'
                  : '#f0b90b',
                color: false ? '#fff' : '#000',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: sending || loading || !amount ? 'not-allowed' : 'pointer',
                opacity: sending || loading || !amount ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {false ? (
                <>
                  <Send size={18} />
                  {sending ? (t('sendingInProgress') || 'Отправка...') : (t('deposit') || 'Депозит')}
                </>
              ) : (
                <>
                  <ArrowUpRight size={18} />
                  {loading ? (t('processingInProgress') || 'Обработка...') : (t('deposit') || 'Депозит')}
                </>
              )}
            </button>
          </>
        )}

        {/* WITHDRAW TAB */}
        {activeTab === 'withdraw' && (
          <>
            {/* Amount Input */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ 
                display: 'block', 
                color: '#848e9c', 
                fontSize: '12px', 
                fontWeight: '500', 
                marginBottom: '8px' 
              }}>
                {t('withdrawAmount')}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '14px',
                    paddingRight: '70px',
                    backgroundColor: '#1e2329',
                    border: '1px solid #2b3139',
                    borderRadius: '10px',
                    color: '#eaecef',
                    fontSize: '16px',
                    fontWeight: '600',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <button
                  onClick={() => setAmount(balance?.exchange_balance?.toString() || '0')}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '5px 10px',
                    backgroundColor: '#2b3139',
                    color: '#f0b90b',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  MAX
                </button>
              </div>
            </div>

            {/* Quick Amounts */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
              {quickAmounts.map((percent) => (
                <button
                  key={percent}
                  onClick={() => setAmount(((balance?.exchange_balance || 0) * percent / 100).toFixed(2))}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: '#1e2329',
                    color: '#848e9c',
                    border: '1px solid #2b3139',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  {percent}%
                </button>
              ))}
            </div>

            {/* Address Input */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                color: '#848e9c', 
                fontSize: '12px', 
                fontWeight: '500', 
                marginBottom: '8px' 
              }}>
                {t('walletAddress')}
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={t('enterWalletAddress')}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#1e2329',
                  border: '1px solid #2b3139',
                  borderRadius: '10px',
                  color: '#eaecef',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Warning */}
            <div style={{
              backgroundColor: '#f6465d15',
              border: '1px solid #f6465d40',
              borderRadius: '10px',
              padding: '12px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
            }}>
              <AlertTriangle size={18} color="#f6465d" style={{ flexShrink: 0, marginTop: '1px' }} />
              <div style={{ color: '#eaecef', fontSize: '11px', lineHeight: '1.4' }}>
                <strong>{t('warning')}:</strong> {t('checkAddress')}
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleWithdrawSubmit}
              disabled={loading || !balance || balance.exchange_balance <= 0 || !amount || !address}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(180deg, #f6465d 0%, #cf304a 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: (loading || !balance || balance.exchange_balance <= 0 || !amount || !address) ? 'not-allowed' : 'pointer',
                opacity: (loading || !balance || balance.exchange_balance <= 0 || !amount || !address) ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <ArrowUpRight size={18} />
              {loading ? t('processingInProgress') : t('withdrawFunds')}
            </button>
          </>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <TransactionHistoryList
            deposits={deposits}
            withdrawals={withdrawals}
            t={t}
            isMobile={true}
          />
        )}

        <DepositConfirmationModal
          isOpen={showDepositModal}
          onClose={() => setShowDepositModal(false)}
          onConfirm={handleDepositConfirm}
          amount={amount}
          currency={selectedCurrency}
          depositAddress={depositAddress}
          sending={sending}
          t={t}
        />
        
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ==================== DESKTOP LAYOUT ====================
  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: COLORS.text, marginBottom: '8px' }}>
          {t('wallet')}
        </h1>
        <p style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
          {t('walletManagement')}
        </p>
      </div>

      {/* Balance Overview - Exchange Balance Only */}
      <div style={{
        backgroundColor: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '32px',
        maxWidth: '400px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>{t('exchangeBalance') || t('totalBalance')}</div>
          <button
            onClick={handleRefreshBalance}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
          >
            <RefreshCw size={16} color={COLORS.textSecondary} style={{ animation: refreshingBalance ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        </div>
        <div style={{ fontSize: '32px', fontWeight: '700', color: COLORS.text }}>
          ${balance?.exchange_balance?.toFixed(2) || '0.00'}
        </div>
      </div>

      {/* Tabs */}
      <WalletTabSwitcher
        activeTab={activeTab}
        onTabChange={setActiveTab}
        t={t}
        isMobile={false}
      />

      {/* Content */}
      <div style={{ display: 'grid', gridTemplateColumns: activeTab === 'history' ? '1fr' : '1fr 1fr', gap: '24px' }}>
        {/* DEPOSIT TAB */}
        {activeTab === 'deposit' && (
          <>
            <div style={{
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '12px',
              padding: '24px',
            }}>
              <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
                {t('selectNetwork')}
              </h3>
              
              <div style={{ marginBottom: '24px' }}>
                <CurrencySelector
                  currencies={currencies}
                  selectedCurrency={selectedCurrency}
                  onSelect={setSelectedCurrency}
                  isMobile={false}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '14px', marginBottom: '8px' }}>
                  {t('amount')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '14px',
                      paddingRight: false ? '80px' : '14px',
                      backgroundColor: COLORS.background,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      color: COLORS.text,
                      fontSize: '16px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  {false && (
                    <button
                      onClick={() => {
                        const max = parseFloat("0");
                        setAmount(selectedCurrency === 'ETH' ? (max * 0.95).toFixed(6) : max.toFixed(2));
                      }}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '6px 12px',
                        backgroundColor: COLORS.card,
                        color: COLORS.primary,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}
                    >
                      MAX
                    </button>
                  )}
                </div>
                {false && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: COLORS.textSecondary }}>
                    {t('availableInWallet')}: {"0"} {selectedCurrency === 'ETH' ? 'ETH' : 'USDT'}
                  </div>
                )}
              </div>

              {/* Action Button - Opens Confirmation Modal */}
              <button
                onClick={handleDepositClick}
                disabled={sending || loading || !amount}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: false ? COLORS.success : COLORS.primary,
                  color: false ? '#fff' : '#000',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: sending || loading || !amount ? 'not-allowed' : 'pointer',
                  opacity: sending || loading || !amount ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {false ? (
                  <>
                    <Send size={18} />
                    {sending ? (t('sendingInProgress') || 'Отправка...') : (t('deposit') || 'Депозит')}
                  </>
                ) : (
                  <>
                    <ArrowUpRight size={18} />
                    {loading ? (t('processingInProgress') || 'Обработка...') : (t('deposit') || 'Депозит')}
                  </>
                )}
              </button>
            </div>

            {/* QR Code Panel */}
            <div style={{
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '12px',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
                {t('qrCodeForDeposit')}
              </h3>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <QRCodeDisplay 
                  address={depositAddress} 
                  size={180} 
                  notConfiguredText={t('error') || 'Error'} 
                />
              </div>
              <p style={{ color: COLORS.textSecondary, fontSize: '13px', textAlign: 'center' }}>
                {t('scanQrForCopy')}
              </p>
            </div>
          </>
        )}

        {/* WITHDRAW TAB */}
        {activeTab === 'withdraw' && (
          <>
            <div style={{
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '12px',
              padding: '24px',
            }}>
              <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
                {t('withdrawFunds')}
              </h3>

              <div style={{
                backgroundColor: COLORS.background,
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px',
              }}>
                              <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>{t('availableForWithdrawal')}</div>
                <div style={{ color: COLORS.text, fontSize: '28px', fontWeight: '700' }}>
                  ${balance?.exchange_balance?.toFixed(2) || '0.00'} <span style={{ fontSize: '14px', color: COLORS.textSecondary }}>USDT</span>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '14px', marginBottom: '8px' }}>
                  {t('amount')} вывода
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '14px',
                      paddingRight: '80px',
                      backgroundColor: COLORS.background,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      color: COLORS.text,
                      fontSize: '16px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <button
                    onClick={() => setAmount(balance?.exchange_balance?.toString() || '0')}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      padding: '6px 12px',
                      backgroundColor: COLORS.card,
                      color: COLORS.primary,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    MAX
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                {quickAmounts.map((percent) => (
                  <button
                    key={percent}
                    onClick={() => setAmount(((balance?.exchange_balance || 0) * percent / 100).toFixed(2))}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: COLORS.background,
                      color: COLORS.textSecondary,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    {percent}%
                  </button>
                ))}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '14px', marginBottom: '8px' }}>
                  {t('walletAddress')}
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={t('enterWalletAddress')}
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    color: COLORS.text,
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{
                backgroundColor: `${COLORS.danger}15`,
                border: `1px solid ${COLORS.danger}`,
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
              }}>
                <AlertTriangle size={20} color={COLORS.danger} style={{ flexShrink: 0 }} />
                <div style={{ color: COLORS.text, fontSize: '13px', lineHeight: '1.5' }}>
                  <strong>{t('warning')}:</strong> {t('checkAddress')}
                </div>
              </div>

              <button
                onClick={handleWithdrawSubmit}
                disabled={loading || !balance || balance.exchange_balance <= 0 || !amount || !address}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: COLORS.danger,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: (loading || !balance || balance.exchange_balance <= 0 || !amount || !address) ? 'not-allowed' : 'pointer',
                  opacity: (loading || !balance || balance.exchange_balance <= 0 || !amount || !address) ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <ArrowUpRight size={18} />
                {loading ? t('processingInProgress') : t('withdrawFunds')}
              </button>
            </div>

            {/* Info Panel */}
            <div style={{
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '12px',
              padding: '24px',
            }}>
              <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
                {t('withdrawalInfo')}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: COLORS.background, borderRadius: '6px' }}>
                  <span style={{ color: COLORS.textSecondary }}>{t('networkFee')}</span>
                  <span style={{ color: COLORS.text, fontWeight: '600' }}>~$2.00</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: COLORS.background, borderRadius: '6px' }}>
                  <span style={{ color: COLORS.textSecondary }}>{t('minWithdrawal')}</span>
                  <span style={{ color: COLORS.text, fontWeight: '600' }}>$10.00</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: COLORS.background, borderRadius: '6px' }}>
                  <span style={{ color: COLORS.textSecondary }}>{t('processingTime')}</span>
                  <span style={{ color: COLORS.text, fontWeight: '600' }}>1-24 {t('hours')}</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div style={{
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            padding: '24px',
          }}>
            <TransactionHistoryList
              deposits={deposits}
              withdrawals={withdrawals}
              t={t}
              isMobile={false}
            />
          </div>
        )}
      </div>
      
      <DepositConfirmationModal
        isOpen={showDepositModal}
        onClose={() => setShowDepositModal(false)}
        onConfirm={handleDepositConfirm}
        amount={amount}
        currency={selectedCurrency}
        depositAddress={depositAddress}
        sending={sending}
        t={t}
      />
      
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default WalletPage;
